import React, { useState, useEffect } from 'react';

const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Brand Stories",
      subtitle: "That Move People",
      description: "Every brand has a story. We craft visual narratives that connect with your audience and turn attention into action.",
      primaryButton: "Start Your Story",
      secondaryButton: "View Portfolio"
    },
    {
      title: "Your Digital Success",
      subtitle: "Partner",
      description: "We build brands that connect, inspire, and sell—combining strategy, design, and content to create measurable results.",
      primaryButton: "Start Your Project",
      secondaryButton: "View Our Work"
    },
    {
      title: "Creative Excellence,",
      subtitle: "Delivered",
      description: "From logos to full identity systems, we bring your vision to life with professional design and strategic clarity.",
      primaryButton: "Get a Quote",
      secondaryButton: "See Our Work"
    }
  ];

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Navigation functions (currently unused but kept for future use)
  // const goToNextSlide = () => {
  //   setCurrentSlide((prev) => (prev + 1) % slides.length);
  // };

  // const goToPrevSlide = () => {
  //   setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  // };

  return (
    <section id="home" className="min-h-screen flex items-center relative bg-brand-blue pt-16 sm:pt-20 md:pt-24 lg:pt-28 xl:pt-32 overflow-hidden">
      {/* Sea Wave Pattern - Hidden on mobile for performance */}
      <div className="absolute inset-0 overflow-hidden hidden sm:block">
        {/* Ocean Wave Layers */}
        <div className="absolute inset-0 opacity-8">
          <svg className="w-full h-full" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="wave1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{stopColor:'rgba(255,255,255,0.08)', stopOpacity:1}} />
                <stop offset="50%" style={{stopColor:'rgba(255,255,255,0.04)', stopOpacity:1}} />
                <stop offset="100%" style={{stopColor:'rgba(255,255,255,0.08)', stopOpacity:1}} />
              </linearGradient>
              <linearGradient id="wave2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{stopColor:'rgba(255,255,255,0.06)', stopOpacity:1}} />
                <stop offset="50%" style={{stopColor:'rgba(255,255,255,0.02)', stopOpacity:1}} />
                <stop offset="100%" style={{stopColor:'rgba(255,255,255,0.06)', stopOpacity:1}} />
              </linearGradient>
              <linearGradient id="wave3" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{stopColor:'rgba(255,255,255,0.04)', stopOpacity:1}} />
                <stop offset="50%" style={{stopColor:'rgba(255,255,255,0.01)', stopOpacity:1}} />
                <stop offset="100%" style={{stopColor:'rgba(255,255,255,0.04)', stopOpacity:1}} />
              </linearGradient>
            </defs>
            {/* Wave Layer 1 - Top */}
            <path d="M0,100 Q200,50 400,100 T800,80 T1200,90 L1200,0 L0,0 Z" fill="url(#wave1)">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; 50,0; 0,0"
                dur="8s"
                repeatCount="indefinite"
              />
            </path>
            {/* Wave Layer 2 - Middle */}
            <path d="M0,200 Q300,150 600,200 T1200,180 L1200,100 L0,100 Z" fill="url(#wave2)">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; -30,0; 0,0"
                dur="6s"
                repeatCount="indefinite"
              />
            </path>
            {/* Wave Layer 3 - Bottom */}
            <path d="M0,300 Q150,250 300,300 T600,280 T900,300 T1200,290 L1200,200 L0,200 Z" fill="url(#wave3)">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; 40,0; 0,0"
                dur="10s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        </div>

        {/* Floating Sea Foam - Reduced for mobile */}
        <div className="absolute top-20 left-20 w-3 h-3 bg-white/15 rounded-full animate-float blur-sm hidden md:block"></div>
        <div className="absolute top-40 right-32 w-2 h-2 bg-white/20 rounded-full animate-float blur-sm hidden md:block" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-32 left-1/3 w-2.5 h-2.5 bg-white/10 rounded-full animate-float blur-sm hidden md:block" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-1/2 right-20 w-1.5 h-1.5 bg-white/25 rounded-full animate-float blur-sm hidden md:block" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/3 left-1/2 w-2 h-2 bg-white/15 rounded-full animate-float blur-sm hidden md:block" style={{animationDelay: '3s'}}></div>
        
        {/* Sea Bubbles - Reduced for mobile */}
        <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-white/30 rounded-full animate-ping hidden md:block"></div>
        <div className="absolute top-2/3 right-1/4 w-0.5 h-0.5 bg-white/25 rounded-full animate-ping hidden md:block" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/4 w-0.5 h-0.5 bg-white/35 rounded-full animate-ping hidden md:block" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-1/3 right-1/3 w-1 h-1 bg-white/20 rounded-full animate-ping hidden md:block" style={{animationDelay: '3s'}}></div>
        
        {/* Water Ripples - Reduced for mobile */}
        <div className="absolute top-1/3 left-1/3 w-8 h-8 border border-white/10 rounded-full animate-ping hidden md:block"></div>
        <div className="absolute top-2/3 right-1/3 w-6 h-6 border border-white/8 rounded-full animate-ping hidden md:block" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-1/4 left-1/2 w-4 h-4 border border-white/12 rounded-full animate-ping hidden md:block" style={{animationDelay: '4s'}}></div>
      </div>
      
      <div className="container-custom relative z-10 px-2 xs:px-3 sm:px-4 md:px-6 lg:px-8">
        <div className="max-w-xs xs:max-w-sm sm:max-w-2xl md:max-w-4xl lg:max-w-5xl mx-auto text-center">
          {/* Slider Container */}
          <div className="relative">
            {/* Slides */}
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {slides.map((slide, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-1 sm:px-2 md:px-4">
                    {/* Main Headline */}
                    <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-medium mb-3 sm:mb-4 md:mb-6 lg:mb-8 leading-tight text-white tracking-tight">
                      <span className="block mb-1 sm:mb-2 md:mb-3 lg:mb-4 animate-fade-in-up" style={{animationDelay: '0.2s'}}>
                        {slide.title}
                      </span>
                      <span className="block animate-fade-in-up text-brand-orange" style={{animationDelay: '0.4s'}}>
                        {slide.subtitle}
                      </span>
                    </h1>
                    
                    {/* Subtitle */}
                    <p className="text-xs xs:text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white/90 mb-4 sm:mb-6 md:mb-8 lg:mb-12 xl:mb-16 leading-relaxed max-w-xs xs:max-w-sm sm:max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto animate-fade-in-up font-extralight tracking-wide px-1 sm:px-2 md:px-4" style={{animationDelay: '0.6s'}}>
                      {slide.description}
                    </p>
                    
                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 lg:gap-6 justify-center items-center animate-fade-in-up px-1 sm:px-2 md:px-4" style={{animationDelay: '0.8s'}}>
                      <button 
                        className="btn-primary w-full sm:w-auto text-xs xs:text-sm sm:text-base md:text-lg px-3 xs:px-4 sm:px-6 md:px-8 py-2 xs:py-2.5 sm:py-3 md:py-4 min-w-[180px] xs:min-w-[200px] sm:min-w-0"
                        onClick={() => window.open('https://wa.me/252618715418', '_blank')}
                      >
                        {slide.primaryButton}
                      </button>
                      
                      <button 
                        className="btn-secondary w-full sm:w-auto text-xs xs:text-sm sm:text-base md:text-lg px-3 xs:px-4 sm:px-6 md:px-8 py-2 xs:py-2.5 sm:py-3 md:py-4 min-w-[180px] xs:min-w-[200px] sm:min-w-0"
                        onClick={() => window.location.href = '/portfolio'}
                      >
                        {slide.secondaryButton}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center space-x-2 sm:space-x-3 mt-4 sm:mt-6 md:mt-8">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-brand-orange scale-125' 
                    : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
